import moment from 'moment';
import { FC, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import { basketSelectors } from '@store/basket/selectors';
import {
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
  useGetSpaSettings,
  useTimeslotAvailability,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { Property } from 'shared-types/Property';

import { SpaDaysBookingForm } from 'voyager';
import type { GuestConfig, AllOptionsConfig } from 'voyager';

import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { useSubmitTreatment } from './hooks/useSubmitTreatment';

type SpaTreatmentBookingV2Props = {
  id: number;
  property: Property;
  language: string;
  onClose: (showMiniBasket: boolean) => void;
};

export const SpaTreatmentBookingV2: FC<SpaTreatmentBookingV2Props> = ({
  id,
  property,
  language,
  onClose,
}) => {
  const { t } = useTranslations();
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams) as any;
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );

  const form = useFormContext<SpaTreatmentFormValues>();

  const { data: spaEntity, isFetching: isLoadingEntity } = useGetSpaTreatment(
    property.id,
    language,
    id,
    moment(journeyParams.spaDate).format('YYYY-MM-DD')
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  const [weekStartDate, setWeekStartDate] = useState<string>(() => {
    const d = moment(selectedDateString);
    return d.clone().startOf('isoWeek').format('YYYY-MM-DD');
  });

  const {
    data: rawTimeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaTreatmentTimeslots(property.id, Number(id), weekStartDate, language);

  const { slotsByDate, availability, timeslotOptions, handleDateSelect, handleWeekNavigate } =
    useTimeslotAvailability({
      rawTimeslots,
      selectedDateString,
      weekStartDate,
      form,
      onWeekNavigate: setWeekStartDate,
    });

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;
  const requestedGuests = journeyParams?.adults || 1;
  const guestCount = Math.max(minGuests, Math.min(maxGuests, requestedGuests));

  const timeslotPanel: AllOptionsConfig | null = useMemo(() => {
    if (timeslotOptions.length === 0) return null;
    return {
      type: 'all' as const,
      title: t('spa.package.arrivalTime'),
      options: [
        {
          id: 'timeslot',
          label: spaEntity?.name || '',
          timeslots: timeslotOptions,
          selectedTimeslot: watchedArrivalTime?.startTime || undefined,
        },
      ],
    };
  }, [timeslotOptions, watchedArrivalTime, spaEntity?.name, t]);

  const guestConfigs: GuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    return Array.from({ length: guestCount }, (_, i) => ({
      guestNumber: i + 1,
      values: {
        title: guests[i]?.title || '',
        firstName: guests[i]?.firstName || '',
        lastName: guests[i]?.lastName || '',
        gender: guests[i]?.gender || '',
      },
      errors: {},
      optionsPanels: i === 0 && timeslotPanel ? [timeslotPanel] : [],
    }));
  }, [watchedGuests, guestCount, timeslotPanel]);

  const genderOptions = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) {
      options.push({ value: 'neutral', label: t('common.genderNeutral') });
    }
    return options;
  }, [showGenderNeutral, t]);

  const handleGuestChange = (guestIndex: number, field: string, value: string) => {
    const currentGuests = form.getValues('guests') || [];
    const updatedGuests = [...currentGuests];
    if (!updatedGuests[guestIndex]) {
      updatedGuests[guestIndex] = { title: '', firstName: '', lastName: '', gender: '' };
    }
    updatedGuests[guestIndex] = { ...updatedGuests[guestIndex], [field]: value };
    form.setValue('guests', updatedGuests);
  };

  const handleTimeslotChange = (
    _guestIndex: number,
    _panelIndex: number,
    _optionId: string,
    timeslotValue: string
  ) => {
    const daySlots = slotsByDate[selectedDateString];
    if (!daySlots) return;
    const slot = daySlots.find((s) => moment(s.startDateTime).format('HH:mm') === timeslotValue);
    if (slot) {
      form.setValue('arrivalTime', {
        startTime: moment(slot.startDateTime).format('HH:mm'),
        endTime: moment(slot.endDateTime).format('HH:mm'),
        startDateTime: moment(slot.startDateTime).format(),
        endDateTime: moment(slot.endDateTime).format(),
      });
    }
  };

  const hasExtras = !!(spaEntity?.extras?.length || spaEntity?.selectionGroups?.length);

  const { handleSubmit, handleNextStep, handleBack } = useSubmitTreatment({
    form,
    spaEntity: spaEntity || undefined,
    propertyId: property.id,
    language,
    id,
    onClose,
    hasExtras,
  });

  const basePrice = spaEntity?.price?.amount || 0;
  const currencyCode = spaEntity?.price?.currencyCode || 'GBP';

  if (isLoadingEntity && !spaEntity) {
    return <Spinner isActive showDelayMessage />;
  }

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;
  const hasNoAvailability =
    !isLoadingTimeslots &&
    (isTimeslotsError ||
      (rawTimeslots && Array.isArray(rawTimeslots) && rawTimeslots.length === 0));

  if (isTelephoneOnly) {
    return (
      <SpaDaysBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        title={spaEntity?.name}
        isLoading={false}
        guestCount={guestCount}
        minGuests={guestCount}
        maxGuests={guestCount}
        onGuestCountChange={() => {}}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        availability={[]}
        onDateSelect={() => {}}
        onWeekNavigate={() => {}}
        guests={[]}
        onExistingGuestSelect={() => {}}
        onGuestChange={() => {}}
        titleOptions={[]}
        genderOptions={[]}
        onAllOptionsTimeslotChange={() => {}}
        onSingleOptionSelect={() => {}}
        onSingleOptionTimeslotChange={() => {}}
        preferencesEnabled={false}
        onPreferencesToggle={() => {}}
        preferenceCategories={[]}
        expandedPreferences={[]}
        onPreferenceCategoryToggle={() => {}}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={() => {}}
        subtitle={t('spa.telephoneOnly', { spaEntity: t('spa.spaTreatment').toLowerCase() })}
      />
    );
  }

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaDaysBookingForm
        imageUrl={spaEntity?.image?.url}
        imageAlt={spaEntity?.name}
        onBack={handleBack}
        title={spaEntity?.name}
        subtitle={hasNoAvailability ? t('list.noAvailability') : undefined}
        isLoading={isLoadingTimeslots}
        guestCount={guestCount}
        minGuests={spaEntity?.minimumGuests || 1}
        maxGuests={spaEntity?.maximumGuests || spaEntity?.minimumGuests || 1}
        onGuestCountChange={() => {}}
        activeDate={weekStartDate}
        selectedDate={selectedDateString}
        availability={availability}
        onDateSelect={handleDateSelect}
        onWeekNavigate={handleWeekNavigate}
        guests={hasNoAvailability ? [] : guestConfigs}
        onExistingGuestSelect={() => {}}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        onAllOptionsTimeslotChange={handleTimeslotChange}
        onSingleOptionSelect={() => {}}
        onSingleOptionTimeslotChange={() => {}}
        preferencesEnabled={false}
        onPreferencesToggle={() => {}}
        preferenceCategories={[]}
        expandedPreferences={[]}
        onPreferenceCategoryToggle={() => {}}
        onPreferenceOptionToggle={() => {}}
        onPreferenceOtherChange={() => {}}
        onPreferenceFreeTextChange={() => {}}
        stickyFooter={
          hasNoAvailability
            ? undefined
            : {
                title: spaEntity?.name || '',
                totalAmount: basePrice * guestCount,
                currencyCode,
                ctaLabel: hasExtras ? t('spa.package.nextStep') : t('spa.book'),
                onCtaClick: hasExtras ? handleNextStep : handleSubmit,
              }
        }
      />
    </>
  );
};
